import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import {
  LayoutDashboard, FileText, Upload, CheckCircle, Clock, AlertCircle,
  Circle, User, Shield, TrendingUp, Percent, Banknote, Lock,
  FolderOpen, ChevronRight, Sparkles, Eye, Calendar
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const docStatusMap = {
  uploaded: { color: 'text-blue-400', bg: 'bg-blue-500/10', border: 'border-blue-500/20', label: 'Uploaded', labelAr: 'تم الرفع', icon: Upload },
  under_review: { color: 'text-amber-400', bg: 'bg-amber-500/10', border: 'border-amber-500/20', label: 'Under Review', labelAr: 'قيد المراجعة', icon: Clock },
  accepted: { color: 'text-emerald-400', bg: 'bg-emerald-500/10', border: 'border-emerald-500/20', label: 'Verified', labelAr: 'تم التحقق', icon: CheckCircle },
  rejected: { color: 'text-red-400', bg: 'bg-red-500/10', border: 'border-red-500/20', label: 'Rejected', labelAr: 'مرفوض', icon: AlertCircle },
  pending: { color: 'text-white/40', bg: 'bg-white/5', border: 'border-white/10', label: 'Pending', labelAr: 'في الانتظار', icon: Circle },
  completed: { color: 'text-emerald-400', bg: 'bg-emerald-500/10', border: 'border-emerald-500/20', label: 'Completed', labelAr: 'مكتمل', icon: CheckCircle },
};

export default function PortalPage() {
  const { lang } = useLanguage();
  const isAr = lang === 'ar';
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const displayFont = isAr ? 'var(--font-ar-display)' : 'var(--font-en-display)';
  const bodyFont = isAr ? 'var(--font-ar-body)' : 'var(--font-en-body)';

  const [activeTab, setActiveTab] = useState('dashboard');
  const [documents, setDocuments] = useState([]);
  const [application, setApplication] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [docType, setDocType] = useState('passport');
  const [expiryDate, setExpiryDate] = useState('');
  const [issueDate, setIssueDate] = useState('');
  const [docNumber, setDocNumber] = useState('');
  const [holderName, setHolderName] = useState('');
  const [uploadErrors, setUploadErrors] = useState(null);
  const [uploadSuccess, setUploadSuccess] = useState('');
  const [docRules, setDocRules] = useState({});
  const [selectedFile, setSelectedFile] = useState(null);
  const [consent, setConsent] = useState(false);
  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const [declaration, setDeclaration] = useState({ accuracy: false, authorize: false, privacy: false });
  const [submitStatus, setSubmitStatus] = useState('');
  const [clientProfile, setClientProfile] = useState('');
  const [profiles, setProfiles] = useState({});

  const fetchData = useCallback(async () => {
    if (!user || user === false) return;
    try {
      const [docsRes, appRes, rulesRes, profRes, myProfileRes] = await Promise.all([
        axios.get(`${API}/portal/documents`, { withCredentials: true }),
        axios.get(`${API}/portal/application`, { withCredentials: true }),
        axios.get(`${API}/portal/doc-rules`, { withCredentials: true }),
        axios.get(`${API}/portal/profiles`, { withCredentials: true }),
        axios.get(`${API}/portal/client-profile`, { withCredentials: true }),
      ]);
      setDocuments(docsRes.data);
      setApplication(appRes.data);
      setDocRules(rulesRes.data);
      setProfiles(profRes.data);
      if (myProfileRes.data?.profile) setClientProfile(myProfileRes.data.profile);
    } catch (err) { console.error(err); }
  }, [user]);

  const selectProfile = async (key) => {
    setClientProfile(key);
    try {
      await axios.put(`${API}/portal/client-profile`, { profile: key }, { withCredentials: true });
    } catch (err) { console.error('Failed to save profile', err); }
  };

  useEffect(() => {
    if (!authLoading && (!user || user === false)) navigate('/login');
    else fetchData();
  }, [user, authLoading, navigate, fetchData]);

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    setSelectedFile(file || null);
    setUploadErrors(null);
    setUploadSuccess('');
  };

  const handleUpload = async (e) => {
    e?.preventDefault();
    if (!selectedFile) return;

    // Frontend validation before sending
    const rules = docRules[docType];
    const frontErrors = [];
    if (rules?.requires_expiry && !expiryDate) {
      frontErrors.push({ code: 'MISSING_EXPIRY', en: `${rules.name_en} requires an expiry date.`, ar: `${rules.name_ar} يتطلب تاريخ انتهاء الصلاحية.` });
    }
    if (!docNumber.trim()) {
      frontErrors.push({ code: 'MISSING_DOC_NUMBER', en: 'Document number / reference is required.', ar: 'رقم المستند / المرجع مطلوب.' });
    }
    if (!holderName.trim()) {
      frontErrors.push({ code: 'MISSING_HOLDER_NAME', en: 'Document holder name is required (must match the document).', ar: 'اسم حامل المستند مطلوب (يجب أن يطابق المستند).' });
    }
    if (!consent) {
      frontErrors.push({ code: 'NO_CONSENT', en: 'You must confirm that the document information is accurate.', ar: 'يجب التأكيد على صحة معلومات المستند.' });
    }
    if (frontErrors.length > 0) {
      setUploadErrors({ message_en: 'Please complete all required fields:', message_ar: 'يرجى إكمال جميع الحقول المطلوبة:', errors: frontErrors });
      return;
    }

    setUploading(true);
    setUploadErrors(null);
    setUploadSuccess('');
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('doc_type', docType);
      formData.append('doc_number', docNumber);
      formData.append('holder_name', holderName);
      formData.append('consent', 'true');
      if (expiryDate) formData.append('expiry_date', expiryDate);
      if (issueDate) formData.append('issue_date', issueDate);
      const { data } = await axios.post(`${API}/portal/documents`, formData, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
      setUploadSuccess(isAr ? data.message_ar : data.message);
      setExpiryDate(''); setIssueDate(''); setDocNumber(''); setHolderName('');
      setSelectedFile(null); setConsent(false);
      const fileInput = document.querySelector('[data-testid="doc-upload-input"]');
      if (fileInput) fileInput.value = '';
      await fetchData();
    } catch (err) {
      if (err.response?.status === 422 && err.response?.data?.detail?.errors) {
        setUploadErrors(err.response.data.detail);
      } else {
        setUploadErrors({ message_en: 'Upload failed. Please try again.', message_ar: 'فشل الرفع. حاول مرة أخرى.', errors: [] });
      }
    } finally { setUploading(false); }
  };

  const handleSubmitApplication = async () => {
    if (!declaration.accuracy || !declaration.authorize || !declaration.privacy) return;
    try {
      await axios.post(`${API}/portal/submit-application`, {}, { withCredentials: true });
      setSubmitStatus('success');
      await fetchData();
    } catch {
      setSubmitStatus('error');
    }
  };

  if (authLoading) {
    return <div className="min-h-screen bg-[#0A1420] flex items-center justify-center"><div className="animate-spin w-8 h-8 border-4 border-[#C9A84C] border-t-transparent rounded-full" /></div>;
  }

  const milestones = application?.milestones || [];
  const completedSteps = milestones.filter(m => m.status === 'completed').length;
  const activeStepIdx = milestones.findIndex(m => m.status === 'pending');
  const progressPct = milestones.length > 0 ? (completedSteps / milestones.length) * 100 : 0;

  const docTypes = Object.entries(docRules).map(([value, rule]) => ({
    value, label: isAr ? rule.name_ar : rule.name_en, group: isAr ? rule.group_ar : rule.group_en,
  }));

  // Group document types
  const docGroups = {};
  docTypes.forEach(dt => {
    if (!docGroups[dt.group]) docGroups[dt.group] = [];
    docGroups[dt.group].push(dt);
  });

  const currentProfileData = profiles[clientProfile];
  const requiredDocs = currentProfileData?.required || [];
  const recommendedDocs = currentProfileData?.recommended || [];

  const tabs = [
    { id: 'dashboard', icon: LayoutDashboard, label: isAr ? 'لوحة التحكم' : 'Dashboard' },
    { id: 'vault', icon: Lock, label: isAr ? 'الخزنة الآمنة' : 'Private Vault' },
    { id: 'timeline', icon: TrendingUp, label: isAr ? 'المراحل' : 'Timeline' },
  ];

  return (
    <div data-testid="portal-page" className="min-h-screen bg-[#F8FAFB] pt-20" style={{ fontFamily: bodyFont }}>
      {/* Background effects */}
      <div className="fixed inset-0 pointer-events-none z-0">
        <div className="absolute top-20 end-0 w-[500px] h-[500px] bg-[#C9A84C]/5 rounded-full blur-[200px]" />
        <div className="absolute bottom-0 start-0 w-[400px] h-[400px] bg-[#0B2F44]/5 rounded-full blur-[150px]" />
      </div>

      <div className="relative z-10 max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Personal Greeting */}
        <div data-testid="portal-greeting" className="mb-8 bg-gradient-to-r from-[#0B2F44] to-[#0D3A54] rounded-2xl p-6 lg:p-8 border border-[#C9D6D9]/20 shadow-lg">
          <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#C9A84C] to-[#A68966] flex items-center justify-center shadow-lg shadow-[#C9A84C]/20">
                <User className="w-7 h-7 text-white" />
              </div>
              <div>
                <p className="text-white/50 text-xs uppercase tracking-widest mb-1">{isAr ? 'مرحباً بعودتك' : 'Welcome back'}</p>
                <h1 className="text-xl lg:text-2xl font-bold text-white" style={{ fontFamily: displayFont }}>
                  {user?.name}
                </h1>
                <p className="text-white/40 text-xs mt-0.5">{user?.email}</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-end">
                <p className="text-white/40 text-xs">{isAr ? 'حالة الطلب' : 'Application Status'}</p>
                <p className="text-[#C9A84C] font-semibold text-sm capitalize">{application?.status?.replace('_', ' ') || 'Not Started'}</p>
              </div>
              <div className="w-px h-10 bg-white/10" />
              <div className="text-end">
                <p className="text-white/40 text-xs">{isAr ? 'المستندات' : 'Documents'}</p>
                <p className="text-white font-semibold text-sm">{documents.length} {isAr ? 'ملف' : 'files'}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="flex flex-col lg:flex-row gap-6">
          {/* Sidebar */}
          <div className="lg:w-56 flex-shrink-0">
            <div className="bg-white rounded-2xl border border-[#D0D8DC]/50 p-2 flex lg:flex-col gap-1 shadow-sm">
              {tabs.map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  data-testid={`portal-tab-${tab.id}`}
                  className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium w-full transition-all ${
                    activeTab === tab.id
                      ? 'bg-[#0B2F44] text-white shadow-md'
                      : 'text-[#4A6572] hover:text-[#0B2F44] hover:bg-[#F8FAFB] border border-transparent'
                  }`}
                >
                  <tab.icon className="w-4 h-4 flex-shrink-0" />
                  <span>{tab.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Main Content */}
          <div className="flex-1 min-w-0">

            {/* ═══ DASHBOARD ═══ */}
            {activeTab === 'dashboard' && (
              <div data-testid="portal-dashboard" className="space-y-6">
                {/* Interactive Progress Timeline */}
                <GlassCard title={isAr ? 'مراحل التمويل العقاري' : 'Mortgage Application Progress'} icon={Sparkles} displayFont={displayFont}>
                  <div className="relative mt-6">
                    {/* Progress line */}
                    <div className="absolute top-5 start-0 end-0 h-0.5 bg-[#D0D8DC] rounded-full" />
                    <div className="absolute top-5 start-0 h-0.5 bg-gradient-to-r from-[#C9A84C] to-[#A68966] rounded-full transition-all duration-700" style={{ width: `${Math.max(progressPct, 5)}%` }} />

                    <div className="relative flex justify-between">
                      {milestones.map((m, i) => {
                        const isCompleted = m.status === 'completed';
                        const isActive = i === activeStepIdx;
                        const cfg = docStatusMap[m.status] || docStatusMap.pending;
                        return (
                          <div key={i} className="flex flex-col items-center text-center" style={{ width: `${100 / milestones.length}%` }}>
                            <div className={`relative w-10 h-10 rounded-full flex items-center justify-center border-2 transition-all ${
                              isCompleted ? 'bg-[#C9A84C] border-[#C9A84C] shadow-lg shadow-[#C9A84C]/30'
                              : isActive ? 'bg-white border-[#C9A84C] shadow-lg shadow-[#C9A84C]/20'
                              : 'bg-[#F8FAFB] border-[#D0D8DC]'
                            }`}>
                              {isCompleted ? <CheckCircle className="w-5 h-5 text-white" />
                                : isActive ? <div className="w-3 h-3 rounded-full bg-[#C9A84C] animate-pulse" />
                                : <Circle className="w-4 h-4 text-[#D0D8DC]" />}
                              {isActive && <div className="absolute -inset-1 rounded-full border border-[#C9A84C]/30 animate-ping" />}
                            </div>
                            <p className={`mt-2 text-[10px] lg:text-xs font-medium leading-tight ${isCompleted ? 'text-[#A68966]' : isActive ? 'text-[#0B2F44]' : 'text-[#4A6572]/50'}`}>
                              {isAr ? m.step_ar : m.step}
                            </p>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  <div className="mt-6 flex items-center justify-between">
                    <span className="text-[#4A6572] text-xs">{isAr ? 'التقدم' : 'Progress'}</span>
                    <span className="text-[#A68966] font-bold text-sm" style={{ fontFamily: 'var(--font-en-mono)' }}>{Math.round(progressPct)}%</span>
                  </div>
                </GlassCard>

                {/* Financial Snapshot + Vault Preview */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Financial Snapshot */}
                  <GlassCard title={isAr ? 'الملخص المالي' : 'Financial Snapshot'} icon={Banknote} displayFont={displayFont}>
                    <div className="grid grid-cols-2 gap-4 mt-4">
                      <FinanceMetric
                        icon={Percent} label={isAr ? 'نسبة القرض للقيمة' : 'Loan-to-Value (LTV)'}
                        value="80%" sub={isAr ? 'الحد الأقصى للمقيمين' : 'Max for residents'}
                      />
                      <FinanceMetric
                        icon={Banknote} label={isAr ? 'الحد المعتمد' : 'Approved Limit'}
                        value="AED 4M" sub={isAr ? 'بناءً على الملف المالي' : 'Based on profile'}
                      />
                      <FinanceMetric
                        icon={TrendingUp} label={isAr ? 'أفضل سعر' : 'Best Rate'}
                        value="3.75%" sub={isAr ? 'ثابت — سنة واحدة' : 'Fixed — 1 Year'}
                      />
                      <FinanceMetric
                        icon={Shield} label={isAr ? 'القسط الشهري التقديري' : 'Est. Monthly Payment'}
                        value="AED 16.5K" sub={isAr ? 'على 25 سنة' : 'Over 25 years'}
                      />
                    </div>
                  </GlassCard>

                  {/* Vault Preview */}
                  <GlassCard title={isAr ? 'الخزنة الآمنة' : 'Your Private Vault'} icon={Lock} displayFont={displayFont}>
                    <div className="grid grid-cols-2 gap-3 mt-4">
                      {documents.length === 0 ? (
                        <div className="col-span-2 text-center py-8">
                          <FolderOpen className="w-10 h-10 text-[#D0D8DC] mx-auto mb-3" />
                          <p className="text-[#4A6572] text-xs">{isAr ? 'لم يتم رفع مستندات بعد' : 'No documents uploaded yet'}</p>
                          <Button onClick={() => setActiveTab('vault')} variant="ghost" className="text-[#A68966] text-xs mt-2 hover:bg-[#A68966]/10">
                            {isAr ? 'رفع مستند' : 'Upload Document'}
                          </Button>
                        </div>
                      ) : (
                        <>
                          {documents.slice(0, 4).map((doc, i) => {
                            const cfg = docStatusMap[doc.status] || docStatusMap.uploaded;
                            const CfgIcon = cfg.icon;
                            return (
                              <div key={i} data-testid={`vault-preview-${i}`} className={`bg-[#F8FAFB] border border-[#D0D8DC]/50 rounded-xl p-3 transition-all hover:scale-[1.02] hover:shadow-md`}>
                                <div className="flex items-center justify-between mb-2">
                                  <FileText className="w-4 h-4 text-[#4A6572]" />
                                  <CfgIcon className={`w-3.5 h-3.5 ${cfg.color}`} />
                                </div>
                                <p className="text-[#0B2F44] text-xs font-medium truncate">{doc.filename}</p>
                                <p className="text-[#4A6572]/60 text-[10px] mt-0.5 capitalize">{doc.doc_type.replace('_', ' ')}</p>
                              </div>
                            );
                          })}
                          {documents.length > 4 && (
                            <button onClick={() => setActiveTab('vault')} className="col-span-2 text-center py-2 text-[#C9A84C] text-xs hover:underline">
                              {isAr ? `+${documents.length - 4} مستندات أخرى` : `+${documents.length - 4} more documents`}
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  </GlassCard>
                </div>
              </div>
            )}

            {/* ═══ PRIVATE VAULT ═══ */}
            {activeTab === 'vault' && (
              <div data-testid="portal-documents" className="space-y-6">

                {/* Profile Selection + Required Documents */}
                <GlassCard title={isAr ? 'نوع ملفك الشخصي' : 'Your Client Profile'} icon={User} displayFont={displayFont}>
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-4">
                    {Object.entries(profiles).map(([key, p]) => (
                      <button key={key} onClick={() => selectProfile(key)} data-testid={`profile-${key}`}
                        className={`p-4 rounded-xl border text-start transition-all ${clientProfile === key ? 'bg-[#0B2F44] text-white border-[#0B2F44] shadow-md' : 'bg-white text-[#4A6572] border-[#D0D8DC]/50 hover:border-[#A68966]/30'}`}>
                        <p className="text-xs font-bold">{isAr ? p.name_ar : p.name_en}</p>
                        <p className={`text-[10px] mt-1 ${clientProfile === key ? 'text-white/60' : 'text-[#4A6572]/60'}`}>
                          {p.required.length} {isAr ? 'مستندات مطلوبة' : 'required docs'}
                        </p>
                      </button>
                    ))}
                  </div>
                </GlassCard>

                {/* Required & Recommended Documents Checklist */}
                {clientProfile && (
                  <GlassCard title={isAr ? 'المستندات المطلوبة للتقديم' : 'Required Documents for Submission'} icon={Shield} displayFont={displayFont}>
                    <p className="text-[#4A6572] text-xs mt-2 mb-4">{isAr ? `ملفك: ${currentProfileData?.name_ar}` : `Profile: ${currentProfileData?.name_en}`}</p>
                    <div className="space-y-3">
                      {/* Required */}
                      <p className="text-[#0B2F44] text-xs font-bold uppercase tracking-wider">{isAr ? 'إجباري' : 'Required'}</p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                        {requiredDocs.map(dt => {
                          const uploaded = documents.find(d => d.doc_type === dt);
                          const rule = docRules[dt];
                          return (
                            <div key={dt} data-testid={`req-doc-${dt}`} className={`flex items-center gap-2.5 p-3 rounded-lg border ${uploaded ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'}`}>
                              {uploaded ? <CheckCircle className="w-4 h-4 text-emerald-600 flex-shrink-0" /> : <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0" />}
                              <span className={`text-xs font-medium ${uploaded ? 'text-emerald-800' : 'text-red-700'}`}>{isAr ? rule?.name_ar : rule?.name_en || dt}</span>
                            </div>
                          );
                        })}
                      </div>
                      {/* Recommended */}
                      {recommendedDocs.length > 0 && (
                        <>
                          <p className="text-[#4A6572] text-xs font-bold uppercase tracking-wider mt-4">{isAr ? 'موصى به' : 'Recommended'}</p>
                          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                            {recommendedDocs.map(dt => {
                              const uploaded = documents.find(d => d.doc_type === dt);
                              const rule = docRules[dt];
                              return (
                                <div key={dt} className={`flex items-center gap-2.5 p-3 rounded-lg border ${uploaded ? 'bg-emerald-50 border-emerald-200' : 'bg-[#F8FAFB] border-[#D0D8DC]/50'}`}>
                                  {uploaded ? <CheckCircle className="w-4 h-4 text-emerald-600 flex-shrink-0" /> : <Circle className="w-4 h-4 text-[#D0D8DC] flex-shrink-0" />}
                                  <span className={`text-xs ${uploaded ? 'text-emerald-800 font-medium' : 'text-[#4A6572]'}`}>{isAr ? rule?.name_ar : rule?.name_en || dt}</span>
                                </div>
                              );
                            })}
                          </div>
                        </>
                      )}
                      {/* Status */}
                      {(() => {
                        const missing = requiredDocs.filter(r => !documents.some(d => d.doc_type === r));
                        return (
                          <div className={`mt-3 p-3 rounded-lg text-xs font-medium ${missing.length === 0 ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'}`}>
                            {missing.length === 0
                              ? (isAr ? 'جميع المستندات الإجبارية مكتملة — يمكنك تقديم الطلب' : 'All required documents complete — you can submit your application')
                              : (isAr ? `متبقي ${missing.length} مستندات إجبارية` : `${missing.length} required documents remaining`)}
                          </div>
                        );
                      })()}
                    </div>
                  </GlassCard>
                )}

                {/* Upload Form */}
                <GlassCard title={isAr ? 'رفع مستند جديد' : 'Upload New Document'} icon={Upload} displayFont={displayFont}>
                  {uploadSuccess && (
                    <div data-testid="upload-success" className="mt-4 bg-emerald-50 border border-emerald-200 rounded-xl p-4 flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                      <p className="text-emerald-800 text-sm font-medium flex-1">{uploadSuccess}</p>
                      <button onClick={() => setUploadSuccess('')} className="text-emerald-400 hover:text-emerald-600 text-xs">✕</button>
                    </div>
                  )}
                  {uploadErrors && (
                    <div data-testid="upload-errors" className="mt-4 bg-red-50 border border-red-200 rounded-xl p-4">
                      <div className="flex items-start gap-3">
                        <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                        <div className="flex-1">
                          <p className="text-red-800 text-sm font-semibold mb-2">{isAr ? uploadErrors.message_ar : uploadErrors.message_en}</p>
                          <ul className="space-y-2">
                            {uploadErrors.errors?.map((err, i) => (
                              <li key={i} className="flex items-start gap-2 text-sm text-red-700 bg-red-100/50 rounded-lg p-2.5">
                                <span className="text-red-400 font-mono text-xs mt-0.5 flex-shrink-0">{err.code}</span>
                                <span>{isAr ? err.ar : err.en}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                        <button onClick={() => setUploadErrors(null)} className="text-red-300 hover:text-red-500 text-xs">✕</button>
                      </div>
                    </div>
                  )}

                  <form onSubmit={handleUpload} className="mt-4 space-y-4">
                    <div>
                      <label className="text-[#4A6572] text-xs font-medium block mb-1.5">{isAr ? 'نوع المستند' : 'Document Type'} <span className="text-red-500">*</span></label>
                      <Select value={docType} onValueChange={(v) => { setDocType(v); setUploadErrors(null); setUploadSuccess(''); }}>
                        <SelectTrigger data-testid="doc-type-select" className="bg-[#F8FAFB] border-[#D0D8DC] text-[#0B2F44] h-12"><SelectValue /></SelectTrigger>
                        <SelectContent className="max-h-[300px]">
                          {Object.entries(docGroups).map(([group, items]) => (
                            <div key={group}>
                              <div className="px-2 py-1.5 text-[10px] font-bold text-[#A68966] uppercase tracking-wider">{group}</div>
                              {items.map(dt => <SelectItem key={dt.value} value={dt.value} className="text-sm">{dt.label}</SelectItem>)}
                            </div>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="text-[#4A6572] text-xs font-medium block mb-1.5">{isAr ? 'اسم حامل المستند (كما في المستند)' : 'Holder Name (as on document)'} <span className="text-red-500">*</span></label>
                        <Input data-testid="doc-holder-name" value={holderName} onChange={e => setHolderName(e.target.value)} placeholder={isAr ? 'الاسم كما يظهر في المستند' : 'Full name as on document'} className="h-11 bg-[#F8FAFB] border-[#D0D8DC] text-[#0B2F44]" />
                      </div>
                      <div>
                        <label className="text-[#4A6572] text-xs font-medium block mb-1.5">{isAr ? 'رقم المستند / المرجع' : 'Document Number / Reference'} <span className="text-red-500">*</span></label>
                        <Input data-testid="doc-number" value={docNumber} onChange={e => setDocNumber(e.target.value)} placeholder={isAr ? 'مثال: AB1234567' : 'e.g. AB1234567'} className="h-11 bg-[#F8FAFB] border-[#D0D8DC] text-[#0B2F44]" />
                      </div>
                    </div>
                    {(docRules[docType]?.requires_expiry || docRules[docType]?.max_age_months) && (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 p-4 bg-[#F8FAFB] rounded-xl border border-[#D0D8DC]/30">
                        {docRules[docType]?.requires_expiry && (
                          <div>
                            <label className="text-[#4A6572] text-xs font-medium block mb-1.5">{isAr ? 'تاريخ انتهاء الصلاحية' : 'Expiry Date'} <span className="text-red-500">*</span></label>
                            <Input data-testid="doc-expiry-date" type="date" value={expiryDate} onChange={e => setExpiryDate(e.target.value)} className="h-11 border-[#D0D8DC] bg-white text-[#0B2F44]" />
                            {docRules[docType]?.min_validity_months > 0 && <p className="text-[10px] text-[#4A6572]/60 mt-1">{isAr ? `صالح ${docRules[docType].min_validity_months} أشهر على الأقل` : `Valid for ${docRules[docType].min_validity_months}+ months`}</p>}
                          </div>
                        )}
                        {docRules[docType]?.max_age_months && (
                          <div>
                            <label className="text-[#4A6572] text-xs font-medium block mb-1.5">{isAr ? 'تاريخ الإصدار' : 'Issue Date'}</label>
                            <Input data-testid="doc-issue-date" type="date" value={issueDate} onChange={e => setIssueDate(e.target.value)} className="h-11 border-[#D0D8DC] bg-white text-[#0B2F44]" />
                            <p className="text-[10px] text-[#4A6572]/60 mt-1">{isAr ? `خلال ${docRules[docType].max_age_months} أشهر` : `Within ${docRules[docType].max_age_months} months`}</p>
                          </div>
                        )}
                      </div>
                    )}
                    <div>
                      <label className="text-[#4A6572] text-xs font-medium block mb-1.5">{isAr ? 'اختر الملف' : 'Select File'} <span className="text-red-500">*</span></label>
                      <Input data-testid="doc-upload-input" type="file" onChange={handleFileSelect} className="bg-[#F8FAFB] border-[#D0D8DC] text-[#0B2F44] h-12 file:text-[#A68966] file:bg-[#A68966]/10 file:border-0 file:rounded-lg file:px-3 file:me-3" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" />
                      {docRules[docType] && <p className="text-[10px] text-[#4A6572]/60 mt-1">{isAr ? 'الصيغ:' : 'Formats:'} {docRules[docType].allowed_ext?.join(', ')} | {isAr ? 'الحد:' : 'Max:'} {docRules[docType].max_size_mb} MB</p>}
                    </div>
                    <div className="flex items-start gap-3 p-4 bg-[#0B2F44]/5 rounded-xl border border-[#0B2F44]/10">
                      <Checkbox data-testid="doc-consent" checked={consent} onCheckedChange={v => setConsent(v)} className="mt-0.5 border-[#A68966] data-[state=checked]:bg-[#A68966] data-[state=checked]:border-[#A68966]" />
                      <label className="text-xs text-[#1A2B35] leading-relaxed">{isAr ? 'أُقر بأن المعلومات والمستند المقدم صحيح ودقيق ومطابق للأصل، وأتحمل كامل المسؤولية عن صحة البيانات.' : 'I confirm the information and document is accurate, authentic, and matches the original. I take full responsibility for the data submitted.'}</label>
                    </div>
                    <Button type="submit" data-testid="doc-upload-btn" disabled={uploading || !selectedFile || !consent} className="w-full bg-[#A68966] hover:bg-[#C9A84C] text-white font-bold py-5 disabled:opacity-40">
                      {uploading ? <span className="flex items-center gap-2"><div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" />{isAr ? 'جاري التحقق...' : 'Validating...'}</span> : <span className="flex items-center gap-2"><Upload className="w-4 h-4" />{isAr ? 'رفع المستند' : 'Upload Document'}</span>}
                    </Button>
                  </form>
                </GlassCard>

                {/* Submit Application to Banks - Declaration */}
                {(() => {
                  if (!clientProfile) return null;
                  const allDone = requiredDocs.every(r => documents.some(d => d.doc_type === r));
                  return allDone ? (
                    <GlassCard title={isAr ? 'تقديم الطلب للبنوك' : 'Submit Application to Banks'} icon={Banknote} displayFont={displayFont}>
                      {submitStatus === 'success' ? (
                        <div className="mt-4 text-center py-8">
                          <CheckCircle className="w-14 h-14 text-emerald-600 mx-auto mb-3" />
                          <p className="text-[#0B2F44] font-bold text-lg" style={{ fontFamily: displayFont }}>{isAr ? 'تم تقديم الطلب بنجاح!' : 'Application Submitted!'}</p>
                          <p className="text-[#4A6572] text-sm mt-1">{isAr ? 'سيراجع فريقنا مستنداتك ويقدمها للبنوك. سنتواصل معك خلال 24 ساعة.' : 'Our team will review and submit to banks. We\'ll contact you within 24 hours.'}</p>
                        </div>
                      ) : (
                        <div className="mt-4 space-y-4">
                          <p className="text-[#4A6572] text-sm">{isAr ? 'جميع المستندات مكتملة. وافق على الإقرار التالي لتقديم طلبك.' : 'All documents complete. Agree to the declaration below to submit.'}</p>
                          <div className="p-5 bg-[#0B2F44]/5 rounded-xl border border-[#0B2F44]/10 space-y-4">
                            <p className="text-[#0B2F44] text-sm font-bold" style={{ fontFamily: displayFont }}>{isAr ? 'إقرار وتفويض' : 'Declaration & Authorization'}</p>
                            <div className="flex items-start gap-3">
                              <Checkbox data-testid="decl-accuracy" checked={declaration.accuracy} onCheckedChange={v => setDeclaration(p => ({...p, accuracy: v}))} className="mt-0.5 border-[#A68966] data-[state=checked]:bg-[#A68966] data-[state=checked]:border-[#A68966]" />
                              <label className="text-xs text-[#1A2B35] leading-relaxed">{isAr ? 'أُقر بأن جميع المعلومات والمستندات المقدمة صحيحة ودقيقة وكاملة وتمثل وضعي المالي الفعلي. أي معلومات غير صحيحة قد تؤدي لرفض الطلب.' : 'I declare all information and documents are true, accurate, complete, and represent my actual financial position. Inaccurate information may lead to rejection.'}</label>
                            </div>
                            <div className="flex items-start gap-3">
                              <Checkbox data-testid="decl-authorize" checked={declaration.authorize} onCheckedChange={v => setDeclaration(p => ({...p, authorize: v}))} className="mt-0.5 border-[#A68966] data-[state=checked]:bg-[#A68966] data-[state=checked]:border-[#A68966]" />
                              <label className="text-xs text-[#1A2B35] leading-relaxed">{isAr ? 'أفوّض جُليد للرهن العقاري (Guleed Mortgage) باستخدام معلوماتي ومستنداتي للتقديم نيابة عني لدى البنوك والمؤسسات المالية للحصول على عروض تمويل عقاري.' : 'I authorize Guleed Mortgage to use my information and documents to apply on my behalf to banks and financial institutions for mortgage offers.'}</label>
                            </div>
                            <div className="flex items-start gap-3">
                              <Checkbox data-testid="decl-privacy" checked={declaration.privacy} onCheckedChange={v => setDeclaration(p => ({...p, privacy: v}))} className="mt-0.5 border-[#A68966] data-[state=checked]:bg-[#A68966] data-[state=checked]:border-[#A68966]" />
                              <label className="text-xs text-[#1A2B35] leading-relaxed">{isAr ? 'أوافق على سياسة الخصوصية وأفهم أن بياناتي ستُعالج وفقاً لقانون حماية البيانات الإماراتي (المرسوم 45/2021).' : 'I agree to the Privacy Policy and understand my data will be processed per UAE Data Protection Law (Decree 45/2021).'}</label>
                            </div>
                          </div>
                          {submitStatus === 'error' && <p className="text-red-600 text-xs">{isAr ? 'حدث خطأ. حاول مرة أخرى.' : 'Error occurred. Try again.'}</p>}
                          <Button data-testid="submit-application-btn" onClick={handleSubmitApplication} disabled={!declaration.accuracy || !declaration.authorize || !declaration.privacy} className="w-full bg-[#0B2F44] hover:bg-[#0D3A54] text-white font-bold py-5 disabled:opacity-40">
                            <Shield className="w-4 h-4 me-2" />{isAr ? 'تقديم الطلب للبنوك' : 'Submit Application to Banks'}
                          </Button>
                        </div>
                      )}
                    </GlassCard>
                  ) : null;
                })()}

                {/* Document Grid - Vault Style */}
                <GlassCard title={isAr ? 'خزنتك الخاصة' : 'Your Private Vault'} icon={Shield} displayFont={displayFont} subtitle={`${documents.length} ${isAr ? 'مستند' : 'documents'}`}>
                  {documents.length === 0 ? (
                    <div className="text-center py-16">
                      <Lock className="w-16 h-16 text-[#D0D8DC] mx-auto mb-4" />
                      <p className="text-[#4A6572] text-sm">{isAr ? 'خزنتك فارغة — ارفع مستنداتك لبدء التحقق' : 'Your vault is empty — upload documents to begin verification'}</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
                      {documents.map((doc, i) => {
                        const cfg = docStatusMap[doc.status] || docStatusMap.uploaded;
                        const CfgIcon = cfg.icon;
                        return (
                          <div key={i} data-testid={`doc-row-${i}`}
                            className={`bg-[#F8FAFB] border border-[#D0D8DC]/50 rounded-xl p-5 transition-all hover:scale-[1.02] hover:shadow-lg hover:border-[#A68966]/30 group`}>
                            <div className="flex items-start justify-between mb-3">
                              <div className="w-10 h-10 rounded-lg bg-[#0B2F44]/5 flex items-center justify-center">
                                <FileText className="w-5 h-5 text-[#4A6572]" />
                              </div>
                              <Badge className={`${cfg.bg} ${cfg.color} border ${cfg.border} text-[10px] font-semibold gap-1`}>
                                <CfgIcon className="w-3 h-3" />
                                {isAr ? cfg.labelAr : cfg.label}
                              </Badge>
                            </div>
                            <p className="text-[#0B2F44] font-medium text-sm truncate">{doc.filename}</p>
                            <div className="flex items-center justify-between mt-2">
                              <p className="text-[#4A6572] text-xs capitalize">{doc.doc_type.replace('_', ' ')}</p>
                              <p className="text-[#4A6572]/50 text-[10px]">{new Date(doc.uploaded_at).toLocaleDateString()}</p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </GlassCard>
              </div>
            )}

            {/* ═══ TIMELINE ═══ */}
            {activeTab === 'timeline' && (
              <div data-testid="portal-progress" className="space-y-6">
                <GlassCard title={isAr ? 'الجدول الزمني للطلب' : 'Application Timeline'} icon={TrendingUp} displayFont={displayFont}>
                  <div className="mt-6 space-y-0">
                    {milestones.map((m, i) => {
                      const isCompleted = m.status === 'completed';
                      const isActive = i === activeStepIdx;
                      const isLast = i === milestones.length - 1;
                      return (
                        <div key={i} className="flex gap-5">
                          {/* Timeline line */}
                          <div className="flex flex-col items-center">
                            <div className={`w-12 h-12 rounded-full flex items-center justify-center border-2 transition-all ${
                              isCompleted ? 'bg-[#C9A84C] border-[#C9A84C] shadow-lg shadow-[#C9A84C]/20'
                              : isActive ? 'bg-white border-[#C9A84C]'
                              : 'bg-[#F8FAFB] border-[#D0D8DC]'
                            }`}>
                              {isCompleted ? <CheckCircle className="w-6 h-6 text-white" />
                                : isActive ? <div className="w-4 h-4 rounded-full bg-[#C9A84C] animate-pulse" />
                                : <span className="text-[#D0D8DC] text-sm font-bold">{i + 1}</span>}
                            </div>
                            {!isLast && (
                              <div className={`w-0.5 h-16 my-1 ${isCompleted ? 'bg-[#C9A84C]/40' : 'bg-[#D0D8DC]/50'}`} />
                            )}
                          </div>

                          {/* Content */}
                          <div className={`pb-8 flex-1 ${isActive ? '' : ''}`}>
                            <div className={`rounded-xl p-4 ${isActive ? 'bg-[#C9A84C]/5 border border-[#C9A84C]/15' : ''}`}>
                              <p className={`font-semibold text-sm ${isCompleted ? 'text-[#A68966]' : isActive ? 'text-[#0B2F44]' : 'text-[#4A6572]/40'}`} style={{ fontFamily: displayFont }}>
                                {isAr ? m.step_ar : m.step}
                              </p>
                              <p className={`text-xs mt-1 ${isCompleted ? 'text-[#4A6572]' : isActive ? 'text-[#4A6572]' : 'text-[#4A6572]/30'}`}>
                                {isCompleted ? (isAr ? 'مكتمل' : 'Completed')
                                  : isActive ? (isAr ? 'جاري التنفيذ حالياً' : 'Currently in progress')
                                  : (isAr ? 'في الانتظار' : 'Pending')}
                              </p>
                              {m.date && <p className="text-[#4A6572]/40 text-[10px] mt-1">{new Date(m.date).toLocaleDateString()}</p>}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </GlassCard>
              </div>
            )}

          </div>
        </div>
      </div>
    </div>
  );
}

function GlassCard({ title, icon: Icon, displayFont, children, subtitle }) {
  return (
    <div className="bg-white rounded-2xl border border-[#D0D8DC]/50 p-6 shadow-sm">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          {Icon && <Icon className="w-5 h-5 text-[#A68966]" />}
          <h3 className="text-[#0B2F44] font-semibold text-sm" style={{ fontFamily: displayFont }}>{title}</h3>
        </div>
        {subtitle && <span className="text-[#4A6572] text-xs">{subtitle}</span>}
      </div>
      {children}
    </div>
  );
}

function FinanceMetric({ icon: Icon, label, value, sub }) {
  return (
    <div className="bg-[#F8FAFB] rounded-xl p-4 border border-[#D0D8DC]/50 hover:border-[#A68966]/30 transition-colors group">
      <Icon className="w-4 h-4 text-[#A68966]/60 mb-2 group-hover:text-[#A68966] transition-colors" />
      <p className="text-[#4A6572] text-[10px] uppercase tracking-wider">{label}</p>
      <p className="text-[#0B2F44] text-lg font-bold mt-1" style={{ fontFamily: 'var(--font-en-mono)' }}>{value}</p>
      <p className="text-[#4A6572]/60 text-[10px] mt-0.5">{sub}</p>
    </div>
  );
}
